/**
 * Spec 105 — PersonaDetailView. Read view of one persona.
 *
 * Header with type + confidence badges + version label ("v2 · current"),
 * dimensions grouped by family (label / weight / detail), and "New version" +
 * "Deactivate"/"Reactivate" actions (hidden for non-admins). Deactivated
 * personas render muted with a "Deactivated" pill.
 */
import type { Persona, PersonaDimensionType } from '../api/personas.js';
import {
  PERSONA_DIMENSION_TYPES,
  DIMENSION_LABELS,
} from '../api/personas.js';
import { PersonaTypeBadge } from './PersonaTypeBadge.js';
import { PersonaConfidenceBadge } from './PersonaConfidenceBadge.js';

interface Props {
  persona: Persona;
  canMutate: boolean;
  onNewVersion: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}

export function PersonaDetailView({
  persona,
  canMutate,
  onNewVersion,
  onDeactivate,
  onReactivate,
}: Props) {
  const familiesPresent = PERSONA_DIMENSION_TYPES.filter((f) =>
    persona.dimensions.some((d) => d.dimension_type === f),
  );

  return (
    <div
      data-testid="persona-detail-view"
      data-persona-id={persona.persona_id}
      className={`rounded-lg border border-gray-200 bg-white p-5 ${
        persona.is_active ? '' : 'opacity-60'
      }`}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-semibold">{persona.name}</h2>
        <PersonaTypeBadge personaType={persona.persona_type} />
        <PersonaConfidenceBadge confidence={persona.confidence} />
        <span
          data-testid="persona-version-label"
          className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
        >
          v{persona.version}
          {persona.is_current_version ? ' · current' : ''}
        </span>
        {!persona.is_active && (
          <span
            data-testid="persona-deactivated-pill"
            className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600"
          >
            Deactivated
          </span>
        )}
      </div>

      {persona.description && (
        <p className="mb-4 text-sm text-gray-600">{persona.description}</p>
      )}

      <div className="space-y-4">
        {familiesPresent.map((family: PersonaDimensionType) => {
          const rows = persona.dimensions.filter(
            (d) => d.dimension_type === family,
          );
          return (
            <div
              key={family}
              data-testid="persona-dimension-family"
              data-family={family}
            >
              <h3 className="mb-1 text-sm font-semibold text-gray-700">
                {DIMENSION_LABELS[family]}
              </h3>
              <ul className="space-y-1">
                {rows.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-baseline gap-2 text-sm"
                  >
                    <span className="font-medium">{d.label}</span>
                    <span className="text-xs text-gray-400">
                      w={d.weight}
                    </span>
                    {d.detail && (
                      <span className="text-gray-500">— {d.detail}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {canMutate && (
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            data-testid="persona-new-version"
            className="hub-btn hub-btn--primary"
            onClick={onNewVersion}
          >
            New version
          </button>
          {persona.is_active ? (
            <button
              type="button"
              data-testid="persona-deactivate"
              className="hub-btn hub-btn--ghost"
              onClick={onDeactivate}
            >
              Deactivate
            </button>
          ) : (
            <button
              type="button"
              data-testid="persona-reactivate"
              className="hub-btn hub-btn--ghost"
              onClick={onReactivate}
            >
              Reactivate
            </button>
          )}
        </div>
      )}
    </div>
  );
}
